/**
 * Utility functions for handling device IDs
 */

/**
 * Normalizes a device ID by adding the '863' prefix if not present
 * @param deviceId - The device ID to normalize
 * @returns The normalized device ID with 863 prefix
 */
export function normalizeDeviceId(deviceId: string): string {
  if (!deviceId) return deviceId;
  
  // If device ID doesn't start with '863', add it
  if (!deviceId.startsWith('863')) {
    return '863' + deviceId;
  }
  
  return deviceId;
}

/**
 * Checks if two device IDs match (considering the 863 prefix)
 * @param id1 - First device ID
 * @param id2 - Second device ID
 * @returns True if the IDs match after normalization
 */
export function deviceIdsMatch(id1: string, id2: string): boolean {
  if (!id1 || !id2) return false;
  
  return normalizeDeviceId(id1) === normalizeDeviceId(id2);
}

/**
 * Gets all possible variations of a device ID (with and without 863 prefix)
 * @param deviceId - The device ID
 * @returns Array of possible device ID variations
 */
export function getDeviceIdVariations(deviceId: string): string[] {
  if (!deviceId) return [];
  
  const normalized = normalizeDeviceId(deviceId); // This now ensures 863 prefix
  const variations = [normalized];
  
  // Add version without 863 prefix if it has one
  if (deviceId.startsWith('863')) {
    variations.push(deviceId.substring(3));
  } else {
    // Original doesn't have prefix, so add it
    variations.push(deviceId);
  }
  
  return Array.from(new Set(variations)); // Remove duplicates
}